import React, { useState, useEffect } from "react";
import Title from "../../../../components/title/Title";
import { MainContainer } from "../../../styles/sharedStyles";
import Section from "../../../../components/section/Section";
import { tokens } from "../../../../../theme";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
} from "@mui/material";
import apiService from "../../../../../ApiService";
import { API_ENDPOINTS } from "../../../../../ApiService";
import InfoTextFieldWithDialog from "../../../../components/tooltip/infoTextFieldWithDialog/InfoTextFieldWithDialog";
import EquipmentListingPage from "./EquipmentListingPage";
import MuscleGroupCheckboxes from "../../../../components/checkboxes/MuscleGroupCheckboxes";
import InfoSelectWithDialog from "../../../../components/tooltip/infoSelectWithDialog/InfoSelectWithDialog";

const EquipmentCreationPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if it's a mobile screen

  // Form state for equipment creation
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // For checkboxes (IDs of selected muscle groups)
  const [selectedMuscleIds, setSelectedMuscleIds] = useState([]);

  // State for the muscle groups available (to display as checkboxes)
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipmentType, setEquipmentType] = useState("");
  const [equipments, setEquipments] = useState([]);

  // Options for equipment type (adjust the values/labels to match your EquipmentType enum)
  const equipmentTypeOptions = [
    { value: "CARDIO", label: "Cardio" },
    { value: "NON_MACHINE", label: "Non-Machine" },
    { value: "GENERAL_MACHINE", label: "General Machine" },
    { value: "UPPER_BODY_MACHINE", label: "Upper Body Machine" },
    { value: "CORE_AND_GLUTES_MACHINE", label: "Core and Glutes Machine" },
    { value: "LOWER_BODY_MACHINE", label: "Lower Body Machine" },
  ];

  // Fetch muscle groups from the backend
  const fetchMuscleGroups = async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.FITNESS_GET_MUSCLE);
      setMuscleGroups(response.data);
    } catch (error) {
      console.error("Error fetching muscle groups:", error);
    }
  };

  // Fetch existing muscle groups from the API
  const fetchEquipments = async () => {
    try {
      const response = await apiService.get(
        API_ENDPOINTS.FITNESS_GET_EQUIPMENT
      );
      setEquipments(response.data);
    } catch (error) {
      console.error("Error fetching muscle groups:", error);
    }
  };

  useEffect(() => {
    fetchMuscleGroups();
    fetchEquipments();
  }, []);

  // Handle muscle group checkbox changes
  const handleCheckboxChange = (e) => {
    const id = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedMuscleIds([...selectedMuscleIds, id]);
    } else {
      setSelectedMuscleIds(selectedMuscleIds.filter((mid) => mid !== id));
    }
  };

  // Handle equipment form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // For demonstration, we hard-code the equipmentType.
      // You could add a dropdown for this as needed.
      const payload = {
        name,
        description,
        equipmentType,
        muscleGroupIds: selectedMuscleIds,
      };
      await apiService.post(API_ENDPOINTS.FITNESS_ADD_EQUIPMENT, payload);
      // Clear the form fields
      setName("");
      setDescription("");
      setEquipmentType("");
      setSelectedMuscleIds([]);
      fetchEquipments();
    } catch (error) {
      console.error("Error creating equipment:", error);
    }
  };

  return (
    <MainContainer>
      <Title title=" Create Equipment" summary="Formulaire" />
      <Section>
        <Paper
          elevation={3}
          sx={{
            p: 1.5, // smaller padding
            mb: 2,
            backgroundColor: colors.primary[400],
          }}
        >
          <Typography variant="body1" color={colors.grey[100]} align="left">
            Please enter the details for the equipment.
          </Typography>
        </Paper>
      </Section>

      <Box
        sx={{
          my: isMobile ? 1 : 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            sx={{ p: isMobile ? 1 : 1.5 }}
          >
            <Box sx={{ p: 1 }}>
              {/* Form Section */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <form onSubmit={handleSubmit}>
                  <InfoTextFieldWithDialog
                    label="Equipment Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    infoTitle="Equipment Name"
                    infoDescription="Name: The equipment name."
                  />
                  <InfoTextFieldWithDialog
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    infoTitle="Description"
                    infoDescription="Description: A brief description."
                  />

                  <InfoSelectWithDialog
                    label="Equipment Type"
                    value={equipmentType}
                    onChange={(e) => setEquipmentType(e.target.value)}
                    infoTitle="Equipment Type Info"
                    infoDescription="Select the equipment type from the available options. Options include CARDIO, NON_MACHINE, GENERAL_MACHINE, etc."
                    options={equipmentTypeOptions}
                    sx={{ mt: 2 }}
                  />

                  <Box sx={{ mt: 2 }}>
                    <MuscleGroupCheckboxes
                      selectedMuscleIds={selectedMuscleIds}
                      setSelectedMuscleIds={setSelectedMuscleIds}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Create Equipment
                  </Button>
                </form>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* EquipmentListingPage component below shows equipments grouped by type */}
      <EquipmentListingPage equipments={equipments} />
    </MainContainer>
  );
};

export default EquipmentCreationPage;
